import { Button, cn, formatDateTime, safeStorage } from '@bixo/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertOctagon, AlertTriangle, Eye, Info, LogOut, X } from 'lucide-react'
import { useState } from 'react'
import { announcementsQuery, impersonationQuery, platformEndpoints } from './api'

/** Shown on every page while Bixo support is inside the workspace. */
export function ImpersonationBanner() {
  const queryClient = useQueryClient()
  const state = useQuery(impersonationQuery)
  const end = useMutation({
    mutationFn: platformEndpoints.endImpersonation,
    onSettled: () => {
      queryClient.clear()
      window.location.assign('/login')
    },
  })

  if (!state.data?.active) return null

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning"
    >
      <Eye className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        <strong className="font-semibold">Bixo support ({state.data.operator_name})</strong> is viewing this
        workspace
        {state.data.read_only ? ' in read-only mode' : ''}. Access ends{' '}
        {formatDateTime(state.data.expires_at)}.
      </span>
      <Button size="sm" variant="secondary" loading={end.isPending} onClick={() => end.mutate()}>
        <LogOut /> End session
      </Button>
    </div>
  )
}

const ICON = { info: Info, warning: AlertTriangle, critical: AlertOctagon }
const TONE = {
  info: 'border-brand-200 bg-brand-50 text-brand-800 dark:border-brand-900 dark:bg-brand-950 dark:text-brand-200',
  warning: 'border-warning/30 bg-warning-soft text-warning',
  critical: 'border-danger/30 bg-danger-soft text-danger',
}

/** Platform announcements, dismissible per browser. */
export function AnnouncementBar() {
  const items = useQuery(announcementsQuery)
  const [dismissed, setDismissed] = useState<number[]>(() => {
    try {
      return JSON.parse(safeStorage.get('bixo.dismissed-announcements') ?? '[]') as number[]
    } catch {
      return []
    }
  })

  const visible = (items.data ?? []).filter((a) => a.level === 'critical' || !dismissed.includes(a.id))
  if (visible.length === 0) return null

  const dismiss = (id: number): void => {
    const next = [...dismissed, id]
    setDismissed(next)
    safeStorage.set('bixo.dismissed-announcements', JSON.stringify(next))
  }

  return (
    <div className="grid gap-2">
      {visible.map((a) => {
        const Icon = ICON[a.level]
        return (
          <div
            key={a.id}
            className={cn('flex items-start gap-3 rounded-xl border px-4 py-2.5 text-sm', TONE[a.level])}
          >
            <Icon className="mt-0.5 size-4 shrink-0" />
            <p className="min-w-0 flex-1">
              <strong className="font-semibold">{a.title}</strong>
              {a.body ? <span className="opacity-90"> · {a.body}</span> : null}
            </p>
            {a.level !== 'critical' && (
              <button
                type="button"
                aria-label="Dismiss"
                className="opacity-70 hover:opacity-100"
                onClick={() => dismiss(a.id)}
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
